import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertTriangle, Clock, Route } from 'lucide-react'
import { workOrdersApi } from '../../services/api'

/**
 * Percorso ottimale: organizza le pratiche selezionate per un giorno.
 * Ordine per comune e via, orari ufficio del giorno (import o poste.it),
 * durata fissa per intervento a partire dall'ora di inizio.
 * Nulla viene salvato finché non si preme "Salva ordine e orari".
 */

interface Voce {
  ordine: number
  wo_id: number
  numero_progressivo: string
  frazionario: string
  denominazione_sito: string
  comune: string
  indirizzo: string
  apertura: string | null
  chiusura: string | null
  chiuso: boolean
  fonte_orari: string | null
  inizio: string | null
  fine: string | null
  avvisi: string[]
}

interface Risultato {
  data: string
  giorno: string
  voci: Voce[]
  fine_giro: string | null
  oltre_chiusura: number
  chiusi: number
}

interface Props {
  ids: number[]
  dataIniziale: string
  onClose: () => void
  onSaved: () => void
}

export default function PianificaGiroModal({ ids, dataIniziale, onClose, onSaved }: Props) {
  const queryClient = useQueryClient()
  const [data, setData] = useState(dataIniziale)
  const [oraInizio, setOraInizio] = useState('08:30')
  const [durata, setDurata] = useState(30)
  const [ris, setRis] = useState<Risultato | null>(null)
  const [loading, setLoading] = useState(false)
  const [salvaOrari, setSalvaOrari] = useState(true)
  const [saving, setSaving] = useState(false)

  const calcola = async () => {
    if (!data) return
    setLoading(true)
    try {
      const res = await workOrdersApi.pianificaGiro({ ids, data, ora_inizio: oraInizio, durata_min: durata })
      setRis(res.data as Risultato)
    } catch {
      toast.error('Errore nel calcolo del giro')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { calcola() }, [data, oraInizio, durata]) // eslint-disable-line react-hooks/exhaustive-deps

  const salva = async () => {
    if (!ris) return
    setSaving(true)
    try {
      await workOrdersApi.salvaOrdineGiro({
        ordini: ris.voci.map(v => ({ wo_id: v.wo_id, ordine: v.ordine, ora: salvaOrari ? v.inizio : null })),
      })
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      queryClient.invalidateQueries({ queryKey: ['work-orders-standard'] })
      toast.success(`Giro salvato: ${ris.voci.length} pratiche ordinate`)
      onSaved()
    } catch {
      toast.error('Errore nel salvataggio del giro')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(8,11,18,0.72)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose() }}>
      <div className="card w-full max-w-5xl animate-fade-up flex flex-col" style={{ maxHeight: '90vh' }}>

        <div className="p-5 border-b shrink-0 space-y-3">
          <div className="flex items-start justify-between">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Route size={16} className="text-blue-600" /> Percorso ottimale
              <span className="text-xs font-normal text-slate-400">({ids.length} pratiche selezionate)</span>
            </h2>
            <button onClick={onClose} disabled={saving} className="btn-ghost px-2">✕</button>
          </div>
          <div className="flex flex-wrap items-end gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="label">Giorno</span>
              <input type="date" className="input !py-1" value={data} onChange={e => setData(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label">Inizio attività</span>
              <input type="time" className="input !py-1" value={oraInizio} onChange={e => setOraInizio(e.target.value || '08:30')} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label">Minuti per intervento</span>
              <input type="number" min={5} max={480} step={5} className="input !py-1 w-24" value={durata}
                onChange={e => setDurata(Math.max(5, Number(e.target.value) || 30))} />
            </label>
            {ris && (
              <p className="text-xs text-slate-500 pb-1">
                {ris.giorno} · fine prevista <b>{ris.fine_giro ?? '—'}</b>
                {ris.oltre_chiusura > 0 && <span className="text-red-600 font-medium"> · {ris.oltre_chiusura} oltre l'orario di chiusura</span>}
                {ris.chiusi > 0 && <span className="text-amber-600 font-medium"> · {ris.chiusi} uffici chiusi quel giorno</span>}
              </p>
            )}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 text-sm">
          {loading || !ris ? (
            <div className="py-10 text-center text-slate-500">Calcolo del giro e verifica orari uffici...</div>
          ) : (
            <div className="border rounded-lg overflow-x-auto text-xs">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b text-left text-slate-600">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Orario</th>
                    <th className="px-3 py-2">Comune</th>
                    <th className="px-3 py-2">Indirizzo</th>
                    <th className="px-3 py-2">Pratica / Sito</th>
                    <th className="px-3 py-2">Ufficio</th>
                    <th className="px-3 py-2">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ris.voci.map((v, i) => {
                    const cambioComune = i > 0 && ris.voci[i - 1].comune !== v.comune
                    const grave = v.chiuso || v.avvisi.some(a => a.startsWith('oltre chiusura'))
                    return (
                      <tr key={v.wo_id} className={grave ? 'bg-red-50' : ''}
                        style={cambioComune ? { borderTop: '2px solid #cbd5e1' } : undefined}>
                        <td className="px-3 py-2 font-mono">{v.ordine}</td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">
                          {v.inizio ? <><Clock size={11} className="inline mr-1 text-slate-400" />{v.inizio}–{v.fine}</> : '—'}
                        </td>
                        <td className="px-3 py-2 font-medium">{v.comune || '—'}</td>
                        <td className="px-3 py-2">{v.indirizzo || '—'}</td>
                        <td className="px-3 py-2">
                          <span className="font-mono">{v.numero_progressivo}</span>
                          {v.denominazione_sito && <span className="text-slate-400"> · {v.denominazione_sito}</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {v.chiuso ? <span className="text-red-600 font-medium">chiuso</span>
                            : v.apertura || v.chiusura ? `${v.apertura ?? '?'}–${v.chiusura ?? '?'}` : '—'}
                          {v.fonte_orari && <span className="text-slate-400"> ({v.fonte_orari})</span>}
                        </td>
                        <td className="px-3 py-2">
                          {v.avvisi.map(a => (
                            <span key={a} className={`inline-flex items-center gap-1 mr-2 ${a.startsWith('oltre') || a.startsWith('ufficio chiuso') ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
                              <AlertTriangle size={11} />{a}
                            </span>
                          ))}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">
            Ordine: prima i comuni con chiusura più vicina, a parità il comune più vicino al precedente; nel comune per via e civico.
            Tempi di spostamento non considerati. Orari ufficio dal file orari importato, altrimenti da poste.it.
          </p>
        </div>

        <div className="px-5 pb-5 pt-3 border-t flex flex-wrap items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 text-xs text-slate-600 mr-auto">
            <input type="checkbox" checked={salvaOrari} onChange={e => setSalvaOrari(e.target.checked)} />
            Salva anche l'orario previsto in "Ore appuntamento"
          </label>
          <button onClick={onClose} disabled={saving} className="btn-secondary">Annulla</button>
          <button onClick={salva} disabled={saving || loading || !ris || ris.voci.length === 0} className="btn-primary">
            {saving ? 'Salvo...' : 'Salva ordine e orari'}
          </button>
        </div>
      </div>
    </div>
  )
}
